import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { ColumnDefinition, TableAction } from '@shared/components/master-table/master-table.component';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { DailyScheduleConfig } from '../daily-schedule/daily-schedule.interface';
import { JourneyConfig } from '../journey/journey.interface';
import {
  WorkCycleConfig,
  WorkCycleCreatePayload,
  WorkCycleDay,
  WorkCycleDayFormValue,
  WorkCycleDayType,
  WorkCycleFormValue,
  WorkCycleUpdatePayload,
} from './work-cycle.interface';

type WorkCycleWorkspaceMode = 'view' | 'create' | 'edit';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-work-cycle-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './work-cycle-detail.component.html',
  styleUrl: './work-cycle-detail.component.scss',
})
export class WorkCycleDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: WorkCycleWorkspaceMode = 'view';
  cycles: WorkCycleConfig[] = [];
  companies: CompaniesRecord[] = [];
  journeys: JourneyConfig[] = [];
  schedules: DailyScheduleConfig[] = [];
  selectedCompanyId = '';
  selectedCycleId: string | null = null;
  selectedCycle: WorkCycleConfig | null = null;
  private baseDataReady = false;
  dataSource = new MatTableDataSource<WorkCycleConfig>([]);
  actions: TableAction<WorkCycleConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  form: WorkCycleFormValue = this.createEmptyForm();
  selectedDayPosition = 1;

  readonly readAccess = { resource: 'workCycles', action: 'read' } as const;
  readonly createAccess = { resource: 'workCycles', action: 'create' } as const;
  readonly updateAccess = { resource: 'workCycles', action: 'update' } as const;
  readonly deleteAccess = { resource: 'workCycles', action: 'delete' } as const;

  readonly dayTypes: WorkCycleDayType[] = ['work', 'rest'];
  readonly cycleLengthPresets = [7, 14, 21, 28, 30];
  readonly restScheduleOptionValue = '__rest__';
  readonly hourSlots = Array.from({ length: 24 }, (_, hour) => ({
    start: hour,
    end: (hour + 1) % 24,
    label: `${hour.toString().padStart(2, '0')}:00 - ${((hour + 1) % 24).toString().padStart(2, '0')}:00`,
  }));

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cycles = this.cycles.map((cycle) => this.normalizeCycle(cycle));
        this.dataSource.data = this.cycles;
        if (this.selectedCycle) {
          this.selectedCycle = this.normalizeCycle(this.selectedCycle);
        }
        this.configureTable();
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.baseDataReady) {
          void this.syncWithRoute();
        }
      });

    this.configureTable();
    void this.initializeFromRoute();
  }

  private async initializeFromRoute(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadBaseData(false, false);
      await this.syncWithRoute();
      this.baseDataReady = true;
    } finally {
      this.isLoading = false;
    }
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get hasMultipleCompanies(): boolean {
    return this.companies.length > 1;
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('workCyclesCatalog.detail.createTitle');
    }

    if (this.isEditMode) {
      return this.t('workCyclesCatalog.detail.editTitle');
    }

    return this.selectedCycle?.name || this.t('workCyclesCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('workCyclesCatalog.detail.createDescription');
    }

    if (this.isEditMode) {
      return this.t('workCyclesCatalog.detail.editDescription');
    }

    return this.t('workCyclesCatalog.detail.description');
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get currentCycleLength(): number {
    const value = Number(this.form.cycleLength || 0);
    return Number.isFinite(value) ? value : 0;
  }

  get selectedFormDay(): WorkCycleDayFormValue | null {
    return this.form.days.find((day) => day.position === this.selectedDayPosition)
      || this.form.days[0]
      || null;
  }

  get workDayCount(): number {
    return this.form.days.filter((day) => this.isWorkDay(day)).length;
  }

  get restDayCount(): number {
    return Math.max(this.currentCycleLength - this.workDayCount, 0);
  }

  get cycleWorkDurationLabel(): string {
    const minutes = this.form.days.reduce(
      (total, day) => total + (this.isWorkDay(day) ? this.getScheduleDurationMinutes(day.dailyScheduleId) : 0),
      0
    );
    return this.formatMinutesAsHours(minutes);
  }

  async refresh(): Promise<void> {
    await this.syncWithRoute();
  }

  async onCompanyChange(): Promise<void> {
    this.selectedCompanyId = this.form.companiesRecordId;
    await this.loadCompanyDependencies();
    this.syncDayReferences();
  }

  onCycleLengthChange(): void {
    const length = Number(this.form.cycleLength || 0);
    const safeLength = Number.isFinite(length) && length > 0 ? Math.min(length, 60) : 0;
    this.form.cycleLength = safeLength || '';
    this.form.days = this.buildDaysArray(safeLength, this.form.days);
    this.selectedDayPosition = Math.min(Math.max(this.selectedDayPosition, 1), Math.max(safeLength, 1));
  }

  increaseCycleLength(): void {
    const current = Number(this.form.cycleLength || 0);
    const next = Math.min((Number.isFinite(current) ? current : 0) + 1, 60);
    this.form.cycleLength = next;
    this.onCycleLengthChange();
  }

  decreaseCycleLength(): void {
    const current = Number(this.form.cycleLength || 0);
    const next = Math.max((Number.isFinite(current) ? current : 0) - 1, 1);
    this.form.cycleLength = next;
    this.onCycleLengthChange();
  }

  applyCycleLengthPreset(length: number): void {
    this.form.cycleLength = length;
    this.onCycleLengthChange();
  }

  isCycleLengthPresetActive(length: number): boolean {
    return this.currentCycleLength === length;
  }

  onDayTypeChange(day: WorkCycleDayFormValue): void {
    if (day.dayType === 'rest') {
      day.journeyId = '';
      day.dailyScheduleId = '';
    }
  }

  onDayScheduleChange(day: WorkCycleDayFormValue, value: string): void {
    if (!value || value === this.restScheduleOptionValue) {
      day.dayType = 'rest';
      day.dailyScheduleId = '';
      day.journeyId = '';
      return;
    }

    day.dayType = 'work';
    day.dailyScheduleId = value;
    const schedule = this.schedules.find((item) => item.dailyScheduleId === value);
    if (!day.journeyId || schedule?.journeyId) {
      day.journeyId = schedule?.journeyId || this.journeys[0]?.journeyId || '';
    }
  }

  selectDay(day: WorkCycleDayFormValue): void {
    this.selectedDayPosition = day.position;
  }

  setDayType(day: WorkCycleDayFormValue, dayType: WorkCycleDayType): void {
    if (dayType === 'rest') {
      this.onDayScheduleChange(day, this.restScheduleOptionValue);
      return;
    }

    const scheduleId = day.dailyScheduleId || this.schedules[0]?.dailyScheduleId || '';
    if (scheduleId) {
      this.onDayScheduleChange(day, scheduleId);
    }
  }

  getDayScheduleSelectionValue(day: WorkCycleDay | WorkCycleDayFormValue): string {
    return this.isWorkDay(day) && day.dailyScheduleId ? day.dailyScheduleId : this.restScheduleOptionValue;
  }

  isWorkDay(day: WorkCycleDay | WorkCycleDayFormValue): boolean {
    return day.dayType === 'work' && !!day.dailyScheduleId;
  }

  isReadOnlyWorkDay(day: WorkCycleDay): boolean {
    return day.dayType === 'work' && !!day.dailyScheduleId;
  }

  trackDay(index: number, day: WorkCycleDayFormValue): number {
    return day.position || index;
  }

  getDayGridColumns(length: number): number {
    return Math.min(Math.max(length || 1, 1), 7);
  }

  chunkDays<T>(days: T[]): T[][] {
    const chunks: T[][] = [];
    for (let index = 0; index < days.length; index += 7) {
      chunks.push(days.slice(index, index + 7));
    }
    return chunks;
  }

  getJourneyName(journeyId: string | null | undefined): string {
    if (!journeyId) {
      return '-';
    }

    return this.journeys.find((journey) => journey.journeyId === journeyId)?.name || '-';
  }

  getJourneyHoursLabel(journeyId: string | null | undefined): string {
    if (!journeyId) {
      return '-';
    }

    const journey = this.journeys.find((item) => item.journeyId === journeyId);
    if (!journey) {
      return '-';
    }

    return this.formatMinutesAsHours(journey.workMinutes);
  }

  getScheduleName(dailyScheduleId: string | null | undefined): string {
    if (!dailyScheduleId) {
      return '-';
    }

    return this.schedules.find((schedule) => schedule.dailyScheduleId === dailyScheduleId)?.name || '-';
  }

  getScheduleTimeRange(dailyScheduleId: string | null | undefined): string {
    if (!dailyScheduleId) {
      return '-';
    }

    const schedule = this.schedules.find((item) => item.dailyScheduleId === dailyScheduleId);
    if (!schedule) {
      return '-';
    }

    if (schedule.blocks?.length) {
      const firstBlock = schedule.blocks[0];
      const suffix = schedule.blocks.length > 1 ? ` +${schedule.blocks.length - 1}` : '';
      return `${firstBlock.startTime} - ${firstBlock.endTime}${suffix}`;
    }

    if (schedule.startTime && schedule.endTime) {
      return `${schedule.startTime} - ${schedule.endTime}`;
    }

    if (schedule.startTime) {
      return schedule.startTime;
    }

    if (schedule.minimumWorkMinutes) {
      return this.formatMinutesAsHours(schedule.minimumWorkMinutes);
    }

    return schedule.name || '-';
  }

  getScheduleDurationLabel(dailyScheduleId: string | null | undefined): string {
    return this.formatMinutesAsHours(this.getScheduleDurationMinutes(dailyScheduleId));
  }

  getScheduleStartLabel(dailyScheduleId: string | null | undefined): string {
    const schedule = this.schedules.find((item) => item.dailyScheduleId === dailyScheduleId);
    return schedule?.blocks?.[0]?.startTime || schedule?.startTime || '-';
  }

  getScheduleEndLabel(dailyScheduleId: string | null | undefined): string {
    const schedule = this.schedules.find((item) => item.dailyScheduleId === dailyScheduleId);
    const blocks = schedule?.blocks || [];
    return blocks[blocks.length - 1]?.endTime || schedule?.endTime || '-';
  }

  getCycleDayName(position: number): string {
    const normalizedPosition = Math.max(Number(position || 1), 1);
    const monday = new Date(Date.UTC(2024, 0, 1 + ((normalizedPosition - 1) % 7)));
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    const label = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(monday);
    return label.replace('.', '').replace(/^./, (letter) => letter.toUpperCase());
  }

  isHourSlotCovered(day: WorkCycleDay | WorkCycleDayFormValue, hourStart: number): boolean {
    return this.getHourSlotFill(day, hourStart).filled;
  }

  getHourSlotFill(
    day: WorkCycleDay | WorkCycleDayFormValue,
    hourStart: number
  ): { filled: boolean; left: number; width: number } {
    if (day.dayType !== 'work' || !day.dailyScheduleId) {
      return { filled: false, left: 0, width: 0 };
    }

    const schedule = this.schedules.find((item) => item.dailyScheduleId === day.dailyScheduleId);
    if (!schedule) {
      return { filled: false, left: 0, width: 0 };
    }

    const slotStart = hourStart * 60;
    const slotEnd = slotStart + 60;
    const intervals = this.getScheduleIntervals(schedule);

    let maxOverlap = 0;
    let overlapStart = slotStart;

    for (const interval of intervals) {
      const sameDay = this.getMinuteOverlap(slotStart, slotEnd, interval.start, interval.end);
      const nextDay = this.getMinuteOverlap(slotStart + 1440, slotEnd + 1440, interval.start, interval.end);
      const overlap = sameDay.minutes >= nextDay.minutes ? sameDay : nextDay;

      if (overlap.minutes > maxOverlap) {
        maxOverlap = overlap.minutes;
        overlapStart = overlap.start;
      }
    }

    if (maxOverlap <= 0) {
      return { filled: false, left: 0, width: 0 };
    }

    const normalizedStart = ((overlapStart % 1440) + 1440) % 1440;
    const left = Math.max(0, Math.min(100, ((normalizedStart - slotStart) / 60) * 100));
    const width = Math.max(0, Math.min(100 - left, (maxOverlap / 60) * 100));

    return { filled: width > 0, left, width };
  }

  getCompanyName(companiesRecordId: string | null | undefined): string {
    if (!companiesRecordId) {
      return '-';
    }

    return this.companies.find((company) => company.companiesRecordId === companiesRecordId)?.name || '-';
  }

  getDayTypeLabel(dayType: WorkCycleDayType): string {
    return this.t(`workCyclesCatalog.dayTypes.${dayType}`);
  }

  getWorkPatternLabel(days: Array<WorkCycleDay | WorkCycleDayFormValue>, cycleLength: number | ''): string {
    const total = Number(cycleLength || 0);
    const work = days.filter((day) => day.dayType === 'work').length;
    const rest = Math.max(total - work, 0);
    return this.t('workCyclesCatalog.labels.workPattern', { work, rest });
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/work-cycles']);
  }

  startCreate(): void {
    this.enterCreateMode(true);
  }

  startEdit(): void {
    this.enterEditMode(true);
  }

  cancelEdit(): void {
    this.exitEditMode(true);
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const createdCycle = await firstValueFrom(
          this.apiService.post<WorkCycleConfig>('labor-config/work-cycles', payload)
        );
        const createdCycleId = createdCycle?.workCycleId || null;
        await this.loadCycles(createdCycleId, false);
        this.showNotification('snackbar-success', this.t('workCyclesCatalog.notifications.created'), 'bottom', 'center');
        if (createdCycleId) {
          void this.router.navigate(['/attendance/work-cycles', createdCycleId]);
        }
      } else if (this.isEditMode && this.selectedCycleId) {
        const payload = this.buildUpdatePayload();
        await firstValueFrom(
          this.apiService.patch(`labor-config/work-cycles/${this.selectedCycleId}`, payload)
        );
        await this.loadCycles(this.selectedCycleId, false);
        this.showNotification('snackbar-success', this.t('workCyclesCatalog.notifications.updated'), 'bottom', 'center');
        void this.router.navigate(['/attendance/work-cycles', this.selectedCycleId]);
      }
    } catch (error) {
      console.error('Error saving work cycle', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('workCyclesCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedCycle || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('workCyclesCatalog.dialog.deleteTitle'),
        message: this.t('workCyclesCatalog.dialog.deleteMessage', { cycle: this.selectedCycle.name }),
        confirmLabel: this.t('workCyclesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedCycleId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`labor-config/work-cycles/${this.selectedCycleId}`));
        await this.loadCycles(null, false);
        this.showNotification('snackbar-success', this.t('workCyclesCatalog.notifications.deleted'), 'bottom', 'center');
        void this.router.navigate(['/attendance/work-cycles']);
      } catch (error) {
        console.error('Error deleting work cycle', error);
        this.showNotification(
          'snackbar-danger',
          this.getApiErrorMessage(error, this.t('workCyclesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  private async loadBaseData(preserveSelection = true, manageLoading = true): Promise<void> {
    if (manageLoading) {
      this.isLoading = true;
    }
    try {
      const companiesResponse = await this.companyDirectory.getAllActive();

      this.companies = this.normalizeCompanies(companiesResponse)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));

      if (
        !preserveSelection ||
        !this.selectedCompanyId ||
        !this.companies.some((company) => company.companiesRecordId === this.selectedCompanyId)
      ) {
        this.selectedCompanyId = this.companies[0]?.companiesRecordId || '';
      }

      await this.loadCompanyDependencies();
    } catch (error) {
      console.error('Error loading base work cycle data', error);
      this.companies = [];
      this.journeys = [];
      this.schedules = [];
      this.selectedCompanyId = '';
    } finally {
      if (manageLoading) {
        this.isLoading = false;
      }
    }
  }

  private async loadCompanyDependencies(): Promise<void> {
    if (!this.selectedCompanyId) {
      this.journeys = [];
      this.schedules = [];
      return;
    }

    try {
      const [journeys, schedules] = await Promise.all([
        firstValueFrom(
          this.apiService.get<JourneyConfig[]>('labor-config/journeys', {
            params: { companiesRecordId: this.selectedCompanyId },
          })
        ),
        firstValueFrom(
          this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules', {
            params: { companiesRecordId: this.selectedCompanyId },
          })
        ),
      ]);

      this.journeys = (journeys || []).filter((journey) => journey.isActive !== false);
      this.schedules = (schedules || []).filter((schedule) => schedule.isActive !== false);
    } catch (error) {
      console.error('Error loading work cycle dependencies', error);
      this.journeys = [];
      this.schedules = [];
    }
  }

  private async loadCycles(
    preferredCycleId: string | null = this.selectedCycleId,
    navigateSelection = true
  ): Promise<void> {
    if (!this.selectedCompanyId) {
      this.cycles = [];
      this.dataSource.data = [];
      this.selectedCycleId = null;
      this.selectedCycle = null;
      this.mode = 'view';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.cycles = (response || []).map((cycle) => this.normalizeCycle(cycle));
      this.dataSource.data = this.cycles;

      const requestedCycle = preferredCycleId
        ? this.cycles.find((cycle) => cycle.workCycleId === preferredCycleId) || null
        : null;

      // The id from the route must win over the first cycle returned for the
      // company selected by default.
      if (requestedCycle) {
        await this.selectCycle(requestedCycle.workCycleId, false, navigateSelection);
      } else if (preferredCycleId) {
        await this.selectCycle(preferredCycleId, false, navigateSelection);
        await this.reloadCyclesForSelectedCompany(preferredCycleId);
      } else if (this.cycles[0]) {
        await this.selectCycle(this.cycles[0].workCycleId, false, navigateSelection);
      } else {
        this.selectedCycleId = null;
        this.selectedCycle = null;
        this.mode = 'view';
      }
    } catch (error) {
      console.error('Error loading work cycles', error);
      this.cycles = [];
      this.dataSource.data = [];
      this.selectedCycleId = null;
      this.selectedCycle = null;
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('workCyclesCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private async reloadCyclesForSelectedCompany(selectedCycleId: string): Promise<void> {
    if (!this.selectedCompanyId) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.cycles = (response || []).map((cycle) => this.normalizeCycle(cycle));
      this.dataSource.data = this.cycles;

      const selectedFromCatalog = this.cycles.find(
        (cycle) => cycle.workCycleId === selectedCycleId
      );
      if (selectedFromCatalog) {
        this.selectedCycle = selectedFromCatalog;
        this.form = this.createFormFromCycle(selectedFromCatalog);
      }
    } catch (error) {
      // The requested detail is already available; keep it editable even if
      // refreshing its company catalog fails.
      console.error('Error syncing work cycles for selected company', error);
    }
  }

  private async selectCycle(workCycleId: string, showLoader = true, navigate = true): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }

    if (showLoader) {
      this.isLoading = true;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<WorkCycleConfig>(`labor-config/work-cycles/${workCycleId}`)
      );
      const normalizedCycle = this.normalizeCycle(response);

      this.selectedCycleId = normalizedCycle.workCycleId;
      this.selectedCycle = normalizedCycle;
      this.selectedCompanyId = normalizedCycle.companiesRecordId || this.selectedCompanyId;
      await this.loadCompanyDependencies();
      this.mode = 'view';
      this.form = this.createFormFromCycle(normalizedCycle);
      if (navigate) {
        void this.router.navigate(['/attendance/work-cycles', normalizedCycle.workCycleId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
    } catch (error) {
      console.error('Error loading work cycle detail', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('workCyclesCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
    } finally {
      if (showLoader) {
        this.isLoading = false;
      }
    }
  }

  private enterCreateMode(navigate = false): void {
    if (!this.hasAccess('create')) {
      return;
    }

    this.mode = 'create';
    this.selectedCycleId = null;
    this.selectedCycle = null;
    this.form = this.createEmptyForm();
    this.selectedDayPosition = 1;
    if (!this.hasMultipleCompanies && this.companies[0]?.companiesRecordId) {
      this.selectedCompanyId = this.companies[0].companiesRecordId;
      this.form.companiesRecordId = this.selectedCompanyId;
      void this.loadCompanyDependencies();
    } else {
      this.selectedCompanyId = '';
      this.journeys = [];
      this.schedules = [];
    }

    if (navigate) {
      void this.router.navigate(['/attendance/work-cycles/new']);
    }
  }

  private enterEditMode(navigate = false): void {
    if (!this.selectedCycle || !this.hasAccess('update')) {
      return;
    }

    this.mode = 'edit';
    this.form = this.createFormFromCycle(this.selectedCycle);
    this.selectedDayPosition = 1;

    if (navigate && this.selectedCycleId) {
      void this.router.navigate(['/attendance/work-cycles', this.selectedCycleId, 'edit'], {
        queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
      });
    }
  }

  private exitEditMode(navigate = false): void {
    if (this.selectedCycle) {
      this.mode = 'view';
      this.form = this.createFormFromCycle(this.selectedCycle);
      if (navigate && this.selectedCycleId) {
        void this.router.navigate(['/attendance/work-cycles', this.selectedCycleId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
      return;
    }

    if (this.cycles.length) {
      const firstCycleId = this.cycles[0].workCycleId;
      if (navigate) {
        void this.router.navigate(['/attendance/work-cycles', firstCycleId]);
      } else {
        void this.selectCycle(firstCycleId, false, false);
      }
      return;
    }

    this.mode = 'view';
    this.form = this.createEmptyForm();
    if (navigate) {
      void this.router.navigate(['/attendance/work-cycles']);
    }
  }

  private async syncWithRoute(): Promise<void> {
    const workCycleId = this.route.snapshot.paramMap.get('workCycleId');
    const companyId = this.route.snapshot.queryParamMap.get('companyId') || '';
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/work-cycles/new');
    const isEditRoute = /\/attendance\/work-cycles\/[^/]+\/edit$/.test(currentUrl);

    if (
      companyId &&
      this.companies.some((company) => company.companiesRecordId === companyId) &&
      companyId !== this.selectedCompanyId
    ) {
      this.selectedCompanyId = companyId;
      await this.loadCompanyDependencies();
    }

    await this.loadCycles(workCycleId, false);

    if (isCreateRoute) {
      this.enterCreateMode(false);
      return;
    }

    if (workCycleId && isEditRoute) {
      this.enterEditMode(false);
    }
  }

  private createEmptyForm(): WorkCycleFormValue {
    return {
      companiesRecordId: '',
      name: '',
      description: '',
      cycleLength: 7,
      days: this.buildDaysArray(7, []),
    };
  }

  private createFormFromCycle(cycle: WorkCycleConfig): WorkCycleFormValue {
    return {
      companiesRecordId: cycle.companiesRecordId || this.selectedCompanyId,
      name: cycle.name,
      description: cycle.description || '',
      cycleLength: cycle.cycleLength,
      days: this.buildDaysArray(
        cycle.cycleLength,
        cycle.days.map((day) => ({
          position: day.position,
          dayType: day.dayType,
          journeyId: day.journeyId || '',
          dailyScheduleId: day.dailyScheduleId || '',
        }))
      ),
    };
  }

  private buildDaysArray(length: number, seed: WorkCycleDayFormValue[]): WorkCycleDayFormValue[] {
    const safeLength = Math.max(0, Math.min(length || 0, 60));
    return Array.from({ length: safeLength }, (_, index) => {
      const current = seed.find((day) => day.position === index + 1);
      return {
        position: index + 1,
        dayType: current?.dayType || 'rest',
        journeyId: current?.journeyId || '',
        dailyScheduleId: current?.dailyScheduleId || '',
      };
    });
  }

  private syncDayReferences(): void {
    const journeyIds = new Set(this.journeys.map((journey) => journey.journeyId));
    const scheduleIds = new Set(this.schedules.map((schedule) => schedule.dailyScheduleId));
    const fallbackJourneyId = this.journeys[0]?.journeyId || '';

    this.form.days = this.form.days.map((day) => ({
      ...day,
      journeyId:
        day.dayType === 'work'
          ? (day.journeyId && journeyIds.has(day.journeyId) ? day.journeyId : fallbackJourneyId)
          : '',
      dailyScheduleId: day.dailyScheduleId && scheduleIds.has(day.dailyScheduleId) ? day.dailyScheduleId : '',
    }));
  }

  private validateForm(): string | null {
    if (!this.form.companiesRecordId) {
      return this.t('orgUnitsCatalog.validationCompany');
    }

    const name = this.normalizeOptionalText(this.form.name);
    const cycleLength = Number(this.form.cycleLength || 0);

    if (!name) {
      return this.t('workCyclesCatalog.notifications.validationName');
    }

    if (!Number.isFinite(cycleLength) || cycleLength <= 0) {
      return this.t('workCyclesCatalog.notifications.validationCycleLength');
    }

    if (this.form.days.length !== cycleLength) {
      return this.t('workCyclesCatalog.notifications.validationDaysLength');
    }

    const invalidWorkDay = this.form.days.find(
      (day) => day.dayType === 'work' && (!day.journeyId || !day.dailyScheduleId)
    );
    if (invalidWorkDay) {
      return this.t('workCyclesCatalog.notifications.validationWorkDayAssignments', {
        day: invalidWorkDay.position,
      });
    }

    return null;
  }

  private buildCreatePayload(): WorkCycleCreatePayload {
    return this.buildPayload();
  }

  private buildUpdatePayload(): WorkCycleUpdatePayload {
    return this.buildPayload();
  }

  private buildPayload(): WorkCycleCreatePayload {
    return {
      companiesRecordId: this.form.companiesRecordId,
      name: this.normalizeOptionalText(this.form.name) || '',
      ...(this.normalizeOptionalText(this.form.description)
        ? { description: this.normalizeOptionalText(this.form.description) }
        : {}),
      cycleLength: Number(this.form.cycleLength),
      repeat: { mode: 'indefinite' },
      days: this.form.days.map((day) => ({
        dayOrder: day.position,
        dayType: day.dayType,
        ...(day.dayType === 'work'
          ? { journeyId: day.journeyId, dailyScheduleId: day.dailyScheduleId }
          : {}),
      })),
    };
  }

  private normalizeCycle(cycle: Partial<WorkCycleConfig>): WorkCycleConfig {
    const days = (cycle.days || []).map((day, index) => ({
      position: this.getDayPosition(day, index),
      dayType: (day.dayType || 'rest') as WorkCycleDayType,
      journeyId: day.journeyId,
      dailyScheduleId: day.dailyScheduleId,
    }));
    const cycleLength = Number(cycle.cycleLength || days.length || 0);
    const isActive = cycle.isActive !== false;

    return {
      workCycleId: cycle.workCycleId || crypto.randomUUID(),
      center: cycle.center || '',
      companiesRecordId: cycle.companiesRecordId,
      name: cycle.name || '',
      description: cycle.description || '',
      cycleLength,
      repeat: { mode: 'indefinite' },
      days,
      isActive,
      companyName: this.getCompanyName(cycle.companiesRecordId),
      cycleLengthLabel: this.t('workCyclesCatalog.labels.cycleLength', { count: cycleLength }),
      workPatternLabel: this.getWorkPatternLabel(days, cycleLength),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private getDayPosition(day: WorkCycleConfig['days'][number], index: number): number {
    const legacyDay = day as WorkCycleConfig['days'][number] & { dayOrder?: number };
    return Number(day.position || legacyDay.dayOrder || index + 1);
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const normalizedValue = value.trim();
    return normalizedValue || undefined;
  }

  private getScheduleDurationMinutes(dailyScheduleId: string | null | undefined): number {
    const schedule = this.schedules.find((item) => item.dailyScheduleId === dailyScheduleId);
    if (!schedule) {
      return 0;
    }

    const intervals = this.getScheduleIntervals(schedule);
    if (intervals.length) {
      return intervals.reduce((total, interval) => total + Math.max(interval.end - interval.start, 0), 0);
    }

    return Number(schedule.minimumWorkMinutes || 0);
  }

  private formatMinutesAsHours(minutes: number | null | undefined): string {
    const value = Number(minutes || 0);
    if (!Number.isFinite(value) || value <= 0) {
      return '-';
    }

    const hours = value / 60;
    return Number.isInteger(hours) ? `${hours} h` : `${hours.toFixed(1)} h`;
  }

  private getScheduleIntervals(schedule: DailyScheduleConfig): Array<{ start: number; end: number }> {
    if (schedule.blocks?.length) {
      return schedule.blocks
        .map((block) => this.createMinuteInterval(block.startTime, block.endTime))
        .filter((interval): interval is { start: number; end: number } => !!interval);
    }

    if (schedule.startTime && schedule.endTime) {
      const interval = this.createMinuteInterval(schedule.startTime, schedule.endTime);
      return interval ? [interval] : [];
    }

    return [];
  }

  private createMinuteInterval(startTime: string | undefined, endTime: string | undefined):
    | { start: number; end: number }
    | null {
    const start = this.timeToMinutes(startTime);
    const end = this.timeToMinutes(endTime);

    if (start === null || end === null) {
      return null;
    }

    const adjustedEnd = end <= start ? end + 1440 : end;
    return { start, end: adjustedEnd };
  }

  private timeToMinutes(value: string | undefined): number | null {
    if (!value) {
      return null;
    }

    const [hours, minutes] = value.split(':').map((part) => Number(part));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private hasMinuteOverlap(
    rangeStart: number,
    rangeEnd: number,
    intervalStart: number,
    intervalEnd: number
  ): boolean {
    return rangeStart < intervalEnd && rangeEnd > intervalStart;
  }

  private getMinuteOverlap(
    rangeStart: number,
    rangeEnd: number,
    intervalStart: number,
    intervalEnd: number
  ): { start: number; minutes: number } {
    const start = Math.max(rangeStart, intervalStart);
    const end = Math.min(rangeEnd, intervalEnd);
    const minutes = Math.max(0, end - start);
    return { start, minutes };
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    return extractApiErrorMessage(error, fallback);
  }

  private configureTable(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('workCyclesCatalog.actions.create'),
        colorClass: 'success',
        auth: this.createAccess,
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: this.readAccess,
        behavior: 'refresh',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: this.updateAccess,
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: this.deleteAccess,
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('workCyclesCatalog.fields.name'), type: 'text', visible: true },
      { def: 'description', label: this.t('workCyclesCatalog.fields.description'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'workPatternLabel', label: this.t('workCyclesCatalog.fields.workPattern'), type: 'text', visible: true },
      {
        def: 'activeLabel',
        label: this.t('workCyclesCatalog.fields.status'),
        type: 'status',
        visible: true,
      },
    ];
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'workCycles', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
